import { useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  GlassBadge,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassEmptyState,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { Building2, CheckCircle2, DoorOpen, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { GET_UNITS } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── Types ── */

interface UnitNode {
  accessKey: number;
  clientCode: string | null;
  linkedAk: number;
  unit: string | null;
  unitnumber: string | null;
  location: string | null;
  floor: string | null;
  ownerTenant: string | null;
  equipment: string | null;
  serviceDate: string | null;
  serviceComplete: number | null;
  accessDenied: number | null;
  lastUpdated: string | null;
  serviceOrder: {
    accessKey: number;
    ticketno: string | null;
    client: { clientName: string | null } | null;
  } | null;
}

interface UnitEdge {
  node: UnitNode;
}

interface UnitConnection {
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  edges: UnitEdge[];
}

interface GetUnitsData {
  serviceOrderUnits: UnitConnection;
}

/* ── Filter options ── */

const completionOptions = [
  { value: '', label: 'All Status' },
  { value: '0', label: 'Incomplete' },
  { value: '1', label: 'Complete' },
];

const accessDeniedOptions = [
  { value: '', label: 'All Access' },
  { value: '0', label: 'No Access Denied' },
  { value: '1', label: 'Access Denied' },
];

const floorOptions = [
  { value: '', label: 'All Floors' },
  { value: 'Ground', label: 'Ground' },
  { value: '1', label: 'Floor 1' },
  { value: '2', label: 'Floor 2' },
  { value: '3', label: 'Floor 3' },
  { value: 'Basement', label: 'Basement' },
];

/* ── Page ── */

export function UnitsPage(): ReactElement {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [floorFilter, setFloorFilter] = useState('');
  const [completionFilter, setCompletionFilter] = useState('');
  const [accessDeniedFilter, setAccessDeniedFilter] = useState('');

  const { data, loading, error } = useQuery<GetUnitsData>(GET_UNITS, {
    variables: {
      first: 100,
      floor: floorFilter || undefined,
      serviceComplete: completionFilter ? parseInt(completionFilter, 10) : undefined,
      accessDenied: accessDeniedFilter ? parseInt(accessDeniedFilter, 10) : undefined,
    },
  });

  const units = data?.serviceOrderUnits.edges ?? [];
  const totalCount = data?.serviceOrderUnits.totalCount ?? 0;

  const filteredUnits = search
    ? units.filter((u) => {
        const s = search.toLowerCase();
        return (
          (u.node.unit?.toLowerCase().includes(s) ?? false) ||
          (u.node.unitnumber?.toLowerCase().includes(s) ?? false) ||
          (u.node.location?.toLowerCase().includes(s) ?? false) ||
          (u.node.equipment?.toLowerCase().includes(s) ?? false) ||
          (u.node.ownerTenant?.toLowerCase().includes(s) ?? false) ||
          String(u.node.accessKey).includes(s)
        );
      })
    : units;

  function handleRowClick(accessKey: number): void {
    void navigate(`/units/${String(accessKey)}`);
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <p className="text-sm text-text-secondary">
        {loading ? (
          'Loading…'
        ) : (
          <>
            <AnimatedCounter value={totalCount} /> unit{totalCount !== 1 ? 's' : ''} total
          </>
        )}
      </p>

      {/* Filters */}
      <GlassCard>
        <GlassCardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1 min-w-0">
              <GlassInput
                placeholder="Search units…"
                value={search}
                onChange={(e): void => {
                  setSearch(e.target.value);
                }}
              />
            </div>
            <div className="flex flex-wrap gap-3 sm:shrink-0">
              <GlassSelect
                label="Floor"
                options={floorOptions}
                value={floorFilter}
                onChange={(e): void => {
                  setFloorFilter(e.target.value);
                }}
              />
              <GlassSelect
                label="Completion"
                options={completionOptions}
                value={completionFilter}
                onChange={(e): void => {
                  setCompletionFilter(e.target.value);
                }}
              />
              <GlassSelect
                label="Access"
                options={accessDeniedOptions}
                value={accessDeniedFilter}
                onChange={(e): void => {
                  setAccessDeniedFilter(e.target.value);
                }}
              />
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Units table */}
      <GlassCard>
        <GlassCardHeader>
          <h2 className="text-base font-semibold text-text">
            Units
            <span className="ml-2 text-sm font-normal text-text-secondary">
              ({String(filteredUnits.length)} shown)
            </span>
          </h2>
        </GlassCardHeader>

        {loading ? (
          <GlassCardContent>
            <div className="space-y-3">
              <SkeletonLoader className="h-10 w-full" />
              <SkeletonLoader className="h-10 w-full" />
              <SkeletonLoader className="h-10 w-full" />
            </div>
          </GlassCardContent>
        ) : error !== undefined ? (
          <GlassCardContent>
            <p className="text-sm text-danger text-center py-8">
              Failed to load units: {error.message}
            </p>
          </GlassCardContent>
        ) : filteredUnits.length === 0 ? (
          <GlassCardContent>
            <GlassEmptyState
              icon={Building2}
              title="No units found"
              description={
                search || floorFilter || completionFilter || accessDeniedFilter
                  ? 'No units match your current filters. Try adjusting your search or filter criteria.'
                  : 'No units have been created yet.'
              }
            />
          </GlassCardContent>
        ) : (
          <div className="overflow-x-auto">
            <GlassTable>
              <GlassTableHead>
                <GlassTableRow>
                  <GlassTableHeaderCell>Unit</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden sm:table-cell">
                    Location
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden md:table-cell">
                    Floor
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden lg:table-cell">
                    Equipment
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden lg:table-cell">
                    Service Order
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell>Status</GlassTableHeaderCell>
                </GlassTableRow>
              </GlassTableHead>
              <GlassTableBody>
                {filteredUnits.map(({ node: unit }) => {
                  const isComplete = unit.serviceComplete === 1;
                  const isAccessDenied = unit.accessDenied === 1;

                  return (
                    <GlassTableRow
                      key={unit.accessKey}
                      className="cursor-pointer"
                      onClick={(): void => {
                        handleRowClick(unit.accessKey);
                      }}
                    >
                      <GlassTableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="font-mono text-xs font-semibold text-primary">
                            {unit.unit ?? `Unit #${String(unit.accessKey)}`}
                          </span>
                          {unit.unitnumber !== null && unit.unitnumber !== unit.unit ? (
                            <span className="text-xs text-text-tertiary">#{unit.unitnumber}</span>
                          ) : null}
                        </div>
                      </GlassTableCell>
                      <GlassTableCell className="text-text-secondary hidden sm:table-cell">
                        {unit.location ?? '—'}
                      </GlassTableCell>
                      <GlassTableCell className="text-text-secondary hidden md:table-cell">
                        {unit.floor ?? '—'}
                      </GlassTableCell>
                      <GlassTableCell className="text-text-secondary hidden lg:table-cell">
                        {unit.equipment ?? '—'}
                      </GlassTableCell>
                      <GlassTableCell className="hidden lg:table-cell">
                        {unit.serviceOrder ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs font-mono text-text-secondary">
                              {unit.serviceOrder.ticketno ?? String(unit.serviceOrder.accessKey)}
                            </span>
                            {/* eslint-disable-next-line eqeqeq */}
                            {unit.serviceOrder.client != null ? (
                              <span className="text-xs text-text-tertiary">
                                {unit.serviceOrder.client.clientName}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </GlassTableCell>
                      <GlassTableCell>
                        <div className="flex flex-col gap-1">
                          {isComplete ? (
                            <GlassBadge variant="success">
                              <CheckCircle2 size={10} />
                              Complete
                            </GlassBadge>
                          ) : (
                            <GlassBadge variant="warning">
                              <XCircle size={10} />
                              Incomplete
                            </GlassBadge>
                          )}
                          {isAccessDenied ? (
                            <GlassBadge variant="danger">
                              <DoorOpen size={10} />
                              Access Denied
                            </GlassBadge>
                          ) : null}
                        </div>
                      </GlassTableCell>
                    </GlassTableRow>
                  );
                })}
              </GlassTableBody>
            </GlassTable>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
